import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledDiv, StyledSpan } from '../../../Theme/CommonStyled';
import DetailsRow from '../../Common/DetailsRow';
import Icon from '../../Common/Icon';
import { convertToPersian, isEmail } from '../../../Utils/common.util';
import InputWithLabel from '../../Common/InputWithLabel';
import GenderPicker from '../GenderPicker';
import Button from '../../Common/Button';
import useProfileStore from '../../../Store/Profile';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router';
import useLandingStore from '../../../Store/Landing';
import DatePicker from '../../Common/DatePicker';
import { StyledBirthdayTitle } from '../style';
import useWindowSize from '../../../Utils/useWindowsSize';
import LoadingBox from '../../Common/LoadingBox';

const EditProfile: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const {
    name: stateName,
    email: stateEmail,
    birthday: stateBirthday,
    sex: stateSex,
    mobile: stateMobile
  } = state || {};
  const [_, height] = useWindowSize();
  const contentRef = useRef(null);

  const landingData = useLandingStore((state) => state.landingData);
  const profileData = useProfileStore((state) => state.profileData);
  const profileLoading = useProfileStore((state) => state.profileLoading);
  const getProfileDataAction = useProfileStore(
    (state) => state.getProfileDataAction
  );
  const editProfileDataAction = useProfileStore(
    (state) => state.editProfileDataAction
  );
  const editProfileLoading = useProfileStore(
    (state) => state.editProfileLoading
  );

  // Use profile data from store if available, otherwise use state
  const name = profileData?.name || stateName;
  const email = profileData?.email || stateEmail;
  const birthday = profileData?.birthday || stateBirthday;
  const sex = profileData?.sex || stateSex;
  const mobile = profileData?.mobile || stateMobile;

  const [nameValue, setNameValue] = useState(name);
  const [sexValue, setSexValue] = useState(sex);
  const [emailValue, setEmailValue] = useState(email);
  const [birthdayValue, setBirthdayValue] = useState(birthday);
  const [hasValidChange, setHasValidChange] = useState(false);

  // Fetch once when branch is known. Do not depend on loading/data or a 400
  // will retrigger this effect forever.
  useEffect(() => {
    if (landingData?.branchId) {
      getProfileDataAction(landingData.branchId);
    }
  }, [landingData?.branchId]);

  // Update form values when profile data changes
  useEffect(() => {
    if (profileData) {
      setNameValue(profileData.name || '');
      setSexValue(profileData.sex || '');
      setEmailValue(profileData.email || '');
      setBirthdayValue(profileData.birthday || '');
    }
  }, [profileData]);

  useEffect(() => {
    setHasValidChange(
      (nameValue !== name ||
        sexValue !== sex ||
        emailValue !== email ||
        new Date(birthday || new Date()).toLocaleString('en-US', {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric'
        }) !==
          new Date(birthdayValue).toLocaleString('en-US', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
          })) &&
        (isEmail(emailValue) || emailValue === email)
    );
  }, [
    nameValue,
    sexValue,
    birthdayValue,
    emailValue,
    name,
    sex,
    email,
    birthday
  ]);

  return (
    <StyledDiv
      height={
        height - 155 < contentRef.current?.clientHeight ? 'unset' : '100%'
      }
      pt={9}
      bg="secondaryBackground"
      px={8}
      position="relative">
      <LoadingBox loading={profileLoading} skeletonType="brand">
        <StyledDiv ref={contentRef}>
          <StyledDiv bg="mainBackground" p="14px" borderRadius="xl" mb={14}>
            <DetailsRow
              color="minorText"
              value={
                <StyledSpan
                  display="flex"
                  alignItems="center"
                  letterSpacing="0.2rem">
                  {convertToPersian(mobile) || '-'}
                  <Icon
                    name="mobile_device"
                    fontSize={8}
                    mr={6}
                    color="minorText"
                  />
                </StyledSpan>
              }
              title={t('profile.mobile')}
            />
          </StyledDiv>
          <InputWithLabel
            label={`${t('profile.name')} :`}
            id="name"
            value={nameValue}
            onChange={(e) => {
              setNameValue(e.target.value);
            }}
            mb={6}
          />
          <StyledDiv mb={10}>
            <GenderPicker status={sexValue} onClick={setSexValue} />
          </StyledDiv>
          <StyledBirthdayTitle>{t('profile.birthday')}</StyledBirthdayTitle>
          <DatePicker
            date={birthdayValue || birthday}
            onChange={setBirthdayValue}
          />
          <InputWithLabel
            label={`${t('profile.email')} :`}
            id="email"
            value={emailValue}
            onChange={(e) => setEmailValue(e.target.value)}
            type="email"
          />
          <StyledDiv mt={6} pb={6}>
            <Button
              disabled={!hasValidChange}
              btnType="primaryGreen"
              loading={editProfileLoading}
              onClick={async (): Promise<void> => {
                const response = await editProfileDataAction({
                  clubBranchId: landingData?.branchId,
                  name: nameValue,
                  sex: sexValue,
                  email: emailValue,
                  birthday: birthdayValue
                });
                response && navigate('/profile');
              }}
              icon={
                <Icon
                  name="save"
                  fontSize={12}
                  color={!hasValidChange ? 'secondaryButtonText' : 'normalText'}
                />
              }>
              {t('profile.confirmChanges')}
            </Button>
          </StyledDiv>
        </StyledDiv>
      </LoadingBox>
    </StyledDiv>
  );
};

export default EditProfile;
